// src/components/reports/CategoryTable.tsx
import React, { useMemo } from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody, LinearProgress, Box, Typography, TableContainer } from '@mui/material';
import { MonthlyRollup, CategoryBreakdown } from '../../types/ReportsTrends';
import { useFormatting } from '../../context/FormattingContext';
import { formatMoney } from '../../utils/formatting';
import { getCategoryColor } from '../../utils/categoryColors';

type Item = CategoryBreakdown['items'][number];

const CategoryTable: React.FC<{
  month: string;
  breakdown: Item[];
  current?: MonthlyRollup;
  prev?: MonthlyRollup;
}> = ({ breakdown, current, prev }) => {
  const formatting = useFormatting();
  const expenses = useMemo(()=> breakdown.filter(b => b.type === 'expense'), [breakdown]);
  const total = current?.expense ?? 0;

  // MoM map by category (prev month expenses by category)
  const prevMap = useMemo(() => {
    // Table only has current month breakdown; for a future enhancement you could add a prev breakdown call.
    // For now, show no MoM if not available.
    return new Map<string, number>();
  }, []);

  const rows = useMemo(()=> {
    const r = expenses
      .map(e => {
        const categoryLabel = e.category?.trim() ? e.category.trim().replace(/\s+/g, ' ') : 'Uncategorized';
        const momPrev = prevMap.get(e.category);
        const mom = momPrev !== undefined && momPrev !== 0 ? (e.amount - momPrev) / momPrev : undefined;
        const pctTotal = total > 0 ? e.amount / total : 0;
        return { ...e, categoryLabel, pctTotal, mom };
      })
      .sort((a,b)=> b.amount - a.amount)
      .slice(0, 10);
    return r;
  }, [expenses, total, prevMap]);

  if (!rows.length) return <Typography sx={{ opacity:.7 }}>No categorized expenses this month.</Typography>;

  return (
    <TableContainer data-testid="category-table-container" sx={{ width: '100%', overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 640 }}>
        <TableHead>
          <TableRow>
            <TableCell>Category</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell align="right">% of Expenses</TableCell>
            <TableCell align="right">MoM</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} hover>
              <TableCell sx={{ maxWidth: { xs: 180, sm: 240 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Box
                    data-testid={`category-color-dot-${i}`}
                    style={{ backgroundColor: getCategoryColor(r.categoryLabel) }}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    component="span"
                    title={r.categoryLabel}
                    data-testid={`category-label-${i}`}
                    sx={{
                      minWidth: 0,
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.categoryLabel}
                  </Box>
                </Box>
              </TableCell>
              <TableCell align="right">
                {formatMoney(r.amount, formatting)}
              </TableCell>
              <TableCell align="right" sx={{ minWidth: 160 }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                  <Box sx={{ flex:1 }}>
                    <LinearProgress
                      data-testid={`category-progress-${i}`}
                      variant="determinate"
                      value={Math.round(r.pctTotal*100)}
                      style={{ '--category-progress-color': getCategoryColor(r.categoryLabel) } as React.CSSProperties}
                      sx={{
                        height: 8,
                        borderRadius:1,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: 'var(--category-progress-color)',
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ width: 44, textAlign:'right' }}>{Math.round(r.pctTotal*100)}%</Box>
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ color: r.mom === undefined ? 'text.secondary' : (r.mom >= 0 ? 'error.main' : 'success.main') }}>
                {r.mom === undefined ? '–' : `${(r.mom*100).toFixed(0)}%`}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CategoryTable;
